import type { AttachmentPickers } from "owa-mail-ribbon-utils";
import type AttachmentMultiPicker from "owa-attachment-filepicker/lib/types/AttachmentMultiPicker";
import { isCopilotAsyncChatEnabled } from "owa-capabilities-definitions/lib/copilotAsyncChatCapability";
import { isVoltronEnabled } from "owa-capabilities-definitions/lib/isVoltronEnabled";
import type { CommandRibbonProps } from "owa-command-ribbon";
import { GovernPriority } from "owa-tti";
import type { GovernedTask } from "owa-tti/lib/types/GovernedTask";
import { owaComputedFn } from "owa-computed-fn";
import { getExtraTopRibbonControlsAfterTabs } from "./util/getExtraTopRibbonControlsAfterTabs";
import { getExtraTopRibbonControlsBeforeTabs } from "./util/getExtraTopRibbonControlsBeforeTabs";
import { getMailControlRendererFunction } from "./util/getMailControlRendererFunction";
import { MailIconColorMap } from "./util/mailIconColorMap";
import { getMailRibbonDefinitionsMLR } from "./components/MLR/mailMultilineRibbonDefinition";
import { getMailRibbonDefinitionsSLR } from "./components/SLR/mailSLRRibbonDefinition";
import { getSelectedTab } from "owa-mail-ribbon-store/lib/selectors/getSelectedTab";
import { lazyModule as lazyActionControlsSectionsModule } from "owa-compose-actioncontrols-sections/lib/sections";
import { lazyModule as lazyFormatControlsSectionsModule } from "owa-compose-formatcontrols-sections/lib/sections";
import { lazyModule as lazyMailComposeControlsSectionsModule } from "owa-mail-compose-control-sections/lib/sections";
import { lazyModule as lazyRibbonSectionsModule } from "owa-mail-ribbon-sections/lib/sections";
import saveSelectedTab from "owa-mail-ribbon-store/lib/actions/saveSelectedTab";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { useCallback } from "react";
import { observer } from "owa-mobx-react";
import { LazyCopilotDab } from "owa-copilot-dab";
import { LazyCopilotDabMailSuggestions } from "owa-copilot-dab-mail";
import { LazyProactiveReadingPaneSuggestion } from "owa-copilot-dab-proactive-reading-pane-sua";
import { isThinking } from "owa-copilot-dab-proactive-reading-pane-sua/lib/selectors/isThinking";
import { isTriggerConditionMet } from "owa-copilot-dab-proactive-reading-pane-sua/lib/selectors/isTriggerConditionMet";
import { determineCopilotDabMailEntrypoint } from "owa-copilot-dab-mail/lib/utils/determineCopilotDabMailEntrypoint";
import { shouldOmitPopover } from "owa-copilot-dab-mail/lib/utils/shouldOmitPopover";
import { isMyOutlookFullPageShowing } from "owa-personal-os-store/lib/selectors/getReadingPaneState";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { onRibbonCustomizerClicked } from "owa-mail-ribbon-command-actions";
import { type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import { LazyOutlookAgentModeButton } from "./components/lazyOutlookAgentModeButton";
import { getComposeViewState } from "owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState";

const getAttachmentPickers = owaComputedFn((attachmentMultiPicker: AttachmentMultiPicker, attachmentInlinePickers: AttachmentMultiPicker): AttachmentPickers => {
    return {
        addFilesAsAttachments: attachmentMultiPicker,
        addImagesInline: attachmentInlinePickers,
    };
});

export const getMailCommandRibbonProps: (attachmentInlinePickers: AttachmentMultiPicker, attachmentMultiPicker: AttachmentMultiPicker) => CommandRibbonProps = owaComputedFn((attachmentInlinePickers, attachmentMultiPicker) => {
    const attachmentPickers = getAttachmentPickers(attachmentMultiPicker, attachmentInlinePickers);
    
    return {
        getSingleLineRibbonDefinition: () => getMailRibbonDefinitionsSLR(attachmentPickers),
        getMultiLineRibbonDefinition: () => getMailRibbonDefinitionsMLR(attachmentPickers),
        iconColorMap: MailIconColorMap,
        ribbonScenarioName: "Mail",
        controlRendererFunction: getMailControlRendererFunction,
        prioritizedSelectedMailTabId: defaultSelectedTabId,
        onTabClick,
        getExtraTopRibbonControlsBeforeTabs,
        getExtraTopRibbonControlsAfterTabs,
        copilotCommandCenter: (<>
                {/* Gate the flight here so the lazy chunk is never fetched when it's off. */}
                {isGlobalFeatureEnabled("agentModeIframe") &&
                !isVoltronEnabled(getModuleContextMailboxInfo()) ? (<LazyOutlookAgentModeButton />) : null}
                <CopilotDab />
            </>),
        thisWindow: window,
        governedTasks: getMailRibbonGovernedTasks(),
        getOnCustomizeClick: getOnRibbonCustomizeClick,
    };
});

const CopilotDab: React.FC = observer(() => {
    const mailboxInfo = getModuleContextMailboxInfo();
    const isMyOutlookShowing = isMyOutlookFullPageShowing();
    
    const shouldOmitDabPopover = useCallback(() => isMyOutlookShowing || shouldOmitPopover(undefined, mailboxInfo), [isMyOutlookShowing, mailboxInfo]);
    
    /* oxlint-disable-next-line react-perf/jsx-no-jsx-as-prop -- (https://aka.ms/OWALintWiki)
     * ESLint resolves proactiveSuggestion but does not inspect JSX inside its conditional initializer;
     * Oxlint does. Suppress until this can be fixed properly.
     * JSX attribute values should not contain other JSX. */
    const proactiveSuggestion = isGlobalFeatureEnabled("copilot-dab-proactive-reading-pane-sua") &&
        isTriggerConditionMet() ? (<LazyProactiveReadingPaneSuggestion {...{ mailboxInfo }}/>) : undefined;
    
    const thinking = isGlobalFeatureEnabled("copilot-dab-proactive-reading-pane-sua") && isThinking();
    const useMonochromeIcon = !!getComposeViewState()?.draftRewrite?.hasAsyncChatSession;
    
    return (<LazyCopilotDab enableAsyncChat={!isMyOutlookShowing && isCopilotAsyncChatEnabled(mailboxInfo)} getEntrypoint={determineCopilotDabMailEntrypoint} isThinking={thinking} mailboxInfo={mailboxInfo} proactiveSuggestion={proactiveSuggestion} shouldOmitPopover={shouldOmitDabPopover} useMonochromeIcon={useMonochromeIcon}>
            <LazyCopilotDabMailSuggestions getEntrypoint={determineCopilotDabMailEntrypoint} mailboxInfo={mailboxInfo}/>
        </LazyCopilotDab>);
}, "CopilotDab");

const getMailRibbonGovernedTasks = owaComputedFn((): GovernedTask[] => {
    return [
        {
            // Import everything in owa-mail-ribbon-sections/lib/sections, such that sections are ready to click.
            task: () => lazyRibbonSectionsModule.importModule("MailCommandRibbonProps"),
            priority: GovernPriority.Ribbon,
            name: "lazyRibbonSectionsModule",
            // Generated by Copilot. Please update with the correct owners.
            ownership: ["lekevin@microsoft.com"],
        },
        {
            // Import everything in owa-compose-formatcontrols-sections/lib/sections, such that sections are ready to click.
            task: () => lazyFormatControlsSectionsModule.importModule("FormatControlsProps"),
            priority: GovernPriority.Compose,
            name: "lazyFormatControlsSectionsModule",
            // Generated by Copilot. Please update with the correct owners.
            ownership: ["biwu@microsoft.com"],
        },
        {
            // Import everything in owa-compose-actioncontrols-sections/lib/sections, such that sections are ready to click.
            task: () => lazyActionControlsSectionsModule.importModule("ActionControlsProps"),
            priority: GovernPriority.Compose,
            name: "lazyActionControlsSectionsModule",
            // Generated by Copilot. Please update with the correct owners.
            ownership: ["biwu@microsoft.com"],
        },
        {
            // Import everything in owa-mail-compose-control-sections/lib/sections, such that sections are ready to click.
            task: () => lazyMailComposeControlsSectionsModule.importModule("MailComposeControlsProps"),
            priority: GovernPriority.Compose,
            name: "lazyMailComposeControlsSectionsModule",
            // Generated by Copilot. Please update with the correct owners.
            ownership: ["biwu@microsoft.com"],
        },
    ];
});

const onTabClick = (tabId: string, targetWindow: Window) => {
    // Saves the selected tab when a new tab is clicked
    saveSelectedTab(tabId, targetWindow);
};

const defaultSelectedTabId = (targetWindow: Window) => getSelectedTab(targetWindow);

const getOnRibbonCustomizeClick = (targetWindow: Window): (() => void) | undefined => {
    const tabId = defaultSelectedTabId(targetWindow);
    
    if (tabId !== 1 && tabId !== 3) {
        return undefined;
    }
    
    return () => onRibbonCustomizerClicked(tabId, getModuleContextMailboxInfo());
};
